import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
  FormControl,
  FormHelperText,
  Checkbox,
} from "@mui/material";
import { Link as RouterLink, useSearchParams, useNavigate } from "react-router-dom";
import { useDI } from "@/infrastructure/di/useDI";
import { useAcceptInvitationViewModel } from "./AcceptInvitationViewModel";
import { humanizeRole } from "../UiLabels";
import { AuthCard, AuthContent, AuthHeader, AuthInfoPanel, AuthPage } from "../layout";
import { useEffect } from "react";
import { LEGAL_DOCUMENTS } from "@/domain/model/LegalDocuments";
import { LegalDocumentLink } from "../components/LegalDocumentLink";


function formatExpiresAt(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Unbekannt";
  }

  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token")?.trim() ?? "";

  const { previewInvitation, acceptInvitation } = useDI();

  const vm = useAcceptInvitationViewModel({
    token,
    previewInvitation,
    acceptInvitation,
    legalVersions: {
      acceptedUserTermsVersion: LEGAL_DOCUMENTS.userTerms.version,
      acceptedPrivacyVersion: LEGAL_DOCUMENTS.privacy.version,
    },
  });


  useEffect(() => {
    if (vm.submitState.type !== "accepted-dashboard") return;

    const dashboardPath = vm.submitState.dashboardPath;

    const timeoutId = window.setTimeout(() => {
      navigate(dashboardPath, { replace: true });
    }, 900);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [navigate, vm.submitState]);

  const readyPreview =
    vm.previewState.type === "ready" ? vm.previewState.preview : null;

  return (
    <AuthPage>
      <AuthCard>
        <AuthContent>
          <AuthHeader
            title="Einladung annehmen"
            subtitle="Erstellen Sie Ihr Passwort und treten Sie dem Konto bei."
          />
          <Stack spacing={3}>
            {vm.previewState.type === "missing-token" && (
              <Alert severity="error">
                Dieser Einladungslink ist ungültig, da der Token fehlt.
              </Alert>
            )}

            {vm.previewState.type === "loading" && (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={22} />
                <Typography color="text.secondary">
                  Einladung wird geladen...
                </Typography>
              </Stack>
            )}

            {vm.previewState.type === "failed" && (
              <Alert severity="error">{vm.previewState.message}</Alert>
            )}

            {vm.submitState.type === "accepted-dashboard" && (
              <Stack spacing={2}>
                <Alert severity="success">
                  Sie wurden erfolgreich angemeldet. Sie werden gleich zum Dashboard
                  weitergeleitet.
                </Alert>

                <Button
                  component={RouterLink}
                  to={vm.submitState.dashboardPath}
                  replace
                  variant="contained"
                  size="large"
                >
                  Zum Dashboard
                </Button>
              </Stack>
            )}

            {vm.submitState.type === "accepted-android" && (
              <Stack spacing={2}>
                <Alert severity="success">
                  Ihr Konto wurde erfolgreich erstellt.
                </Alert>

                <AuthInfoPanel>
                  <Stack spacing={1}>
                    <Typography fontWeight={600}>
                      Bitte in der Android-App anmelden
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Die Einladung für <strong>{vm.submitState.email}</strong> wurde
                      angenommen. Dieses Benutzerkonto ist für die mobile Arbeit vorgesehen.
                      Öffnen Sie jetzt die Logami Android-App und melden Sie sich mit Ihrer
                      E-Mail-Adresse und Ihrem neuen Passwort an.
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Falls die App noch nicht installiert ist, erhalten Sie den
                      Installationslink von Ihrem Administrator.
                    </Typography>
                  </Stack>
                </AuthInfoPanel>
              </Stack>
            )}

            {readyPreview &&
              vm.submitState.type !== "accepted-dashboard" &&
              vm.submitState.type !== "accepted-android" && (
                <Stack
                  component="form"
                  spacing={3}
                  onSubmit={(event) => {
                    event.preventDefault();
                    void vm.actions.submit();
                  }}
                >
                  <AuthInfoPanel>
                    <Typography variant="body2" color="text.secondary">
                      Eingeladene E-Mail-Adresse
                    </Typography>

                    <Typography fontWeight={600}>{readyPreview.email}</Typography>

                    <Typography variant="body2" color="text.secondary">
                      Rolle: {humanizeRole(readyPreview.role)}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Gültig bis: {formatExpiresAt(readyPreview.expiresAt)}
                    </Typography>
                  </AuthInfoPanel>

                  {vm.submitState.type === "failed" && (
                    <Alert severity="error">{vm.submitState.message}</Alert>
                  )}

                  <Stack spacing={2}>
                    <TextField
                      label="Passwort"
                      type="password"
                      value={vm.values.password}
                      onChange={(event) =>
                        vm.actions.setField("password", event.target.value)
                      }
                      error={Boolean(vm.fieldErrors.password)}
                      helperText={vm.fieldErrors.password}
                      autoComplete="new-password"
                      fullWidth
                    />

                    <TextField
                      label="Passwort bestätigen"
                      type="password"
                      value={vm.values.confirmPassword}
                      onChange={(event) =>
                        vm.actions.setField(
                          "confirmPassword",
                          event.target.value,
                        )
                      }
                      error={Boolean(vm.fieldErrors.confirmPassword)}
                      helperText={vm.fieldErrors.confirmPassword}
                      autoComplete="new-password"
                      fullWidth
                    />
                  </Stack>

                  {readyPreview.requiresPerson && (
                    <>
                      <Divider />

                      <Stack spacing={2}>
                        <Stack spacing={0.5}>
                          <Typography variant="h6" component="h2">
                            Persönliche Angaben
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Diese Einladung ist noch keiner bestehenden Person
                            zugeordnet. Bitte legen Sie das Personenprofil für
                            dieses Konto an.
                          </Typography>
                        </Stack>

                        <TextField
                          label="Anzeigename"
                          value={vm.values.person.displayName}
                          onChange={(event) =>
                            vm.actions.setPersonField(
                              "displayName",
                              event.target.value,
                            )
                          }
                          error={Boolean(vm.fieldErrors["person.displayName"])}
                          helperText={vm.fieldErrors["person.displayName"]}
                          autoComplete="name"
                          fullWidth
                          required
                        />

                        <TextField
                          label="Bevorzugter Name"
                          value={vm.values.person.preferredName}
                          onChange={(event) =>
                            vm.actions.setPersonField(
                              "preferredName",
                              event.target.value,
                            )
                          }
                          error={Boolean(vm.fieldErrors["person.preferredName"])}
                          helperText={vm.fieldErrors["person.preferredName"]}
                          autoComplete="nickname"
                          fullWidth
                        />

                        <TextField
                          label="Mobiltelefon"
                          value={vm.values.person.mobilePhoneE164}
                          onChange={(event) =>
                            vm.actions.setPersonField(
                              "mobilePhoneE164",
                              event.target.value,
                            )
                          }
                          error={Boolean(
                            vm.fieldErrors["person.mobilePhoneE164"],
                          )}
                          helperText={
                            vm.fieldErrors["person.mobilePhoneE164"] ??
                            "Bitte verwenden Sie das internationale Format, zum Beispiel +491701234567."
                          }
                          autoComplete="tel"
                          fullWidth
                        />
                      </Stack>
                    </>
                  )}

                  <FormControl
                    error={Boolean(vm.fieldErrors.acceptLegal)}
                    component="fieldset"
                  >
                    <Box
                      onClick={() =>
                        vm.actions.setField("acceptLegal", !vm.values.acceptLegal)
                      }
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        cursor: "pointer",
                        borderRadius: 1,
                        p: 1,
                        ml: -1,
                        userSelect: "none",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <Checkbox
                        checked={vm.values.acceptLegal}
                        onChange={(event) =>
                          vm.actions.setField("acceptLegal", event.target.checked)
                        }
                        onClick={(event) => event.stopPropagation()}
                        slotProps={{
                          input: {
                            "aria-describedby": vm.fieldErrors.acceptLegal
                              ? "accept-invitation-legal-error"
                              : undefined,
                          },
                        }}
                        sx={{ mt: -1 }}
                      />

                      <Typography
                        variant="body2"
                        sx={{ pt: 0.5 }}
                        onClick={(event) => {
                          const target = event.target as HTMLElement;

                          if (target.closest("a")) {
                            event.stopPropagation();
                          }
                        }}
                      >
                        Ich akzeptiere die{" "}
                        <LegalDocumentLink document={LEGAL_DOCUMENTS.userTerms} /> und habe die{" "}
                        <LegalDocumentLink document={LEGAL_DOCUMENTS.privacy} /> zur Kenntnis
                        genommen.
                      </Typography>
                    </Box>

                    {vm.fieldErrors.acceptLegal ? (
                      <FormHelperText id="accept-invitation-legal-error" sx={{ ml: 0 }}>
                        {vm.fieldErrors.acceptLegal}
                      </FormHelperText>
                    ) : null}
                  </FormControl>

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!vm.canSubmit}
                  >
                    {vm.submitState.type === "submitting"
                      ? "Einladung wird angenommen..."
                      : "Einladung annehmen"}
                  </Button>
                </Stack>
              )}
          </Stack>
        </AuthContent>
      </AuthCard>
    </AuthPage>
  );
}
